import { axiosBaseQuery } from "@/shared/api/base-query";
import { streamsApiInstance } from "@/shared/api/axios";
import { createApi } from "@reduxjs/toolkit/query/react";
import { createCameraDailyCache } from "../lib/cache/camera-daily-cache";

const baseQuery = axiosBaseQuery(streamsApiInstance);
const recordingCamerasCache = createCameraDailyCache<number[]>(
  "dayAvailableRecords"
);

type BaseQueryResponse<T> =
  | { data: T }
  | { error: { status: number; data: unknown } };

export const streamsApi = createApi({
  reducerPath: "streamsApi",
  baseQuery,
  endpoints: (builder) => ({
    frameAt: builder.query({
      query: ({ cameraId, timestamp, format = "webp", quality = 0.5 }) => ({
        url: `/frame_reader/v1/frame_at`,
        method: "GET",
        params: {
          camera_id: cameraId,
          timestamp: timestamp.replace(" +", "+"),
          format,
          quality,
        },
      }),
    }),
    getRecordingCameras: builder.query({
      queryFn: async ({ atTime }, _queryApi, _extraOptions, fetchWithBQ) => {
        try {
          recordingCamerasCache.clearExpiredCache();

          const cachedData = recordingCamerasCache.get(atTime);
          if (cachedData !== null) {
            return { data: cachedData };
          }

          const response = (await fetchWithBQ({
            url: "/frame_reader/v1/day_available_records",
            method: "GET",
            params: {
              at_time: atTime,
            },
          })) as BaseQueryResponse<number[]>;

          if ("data" in response) {
            recordingCamerasCache.set(atTime, response.data);
          }

          return response;
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error,
            },
          };
        }
      },
    }),
  }),
});

export const { useFrameAtQuery, useGetRecordingCamerasQuery } = streamsApi;
